import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, Wand2, FileText, CalendarDays, Gauge, Plug, Activity, PenSquare, Plus
} from 'lucide-react';
import Button from '../../components/ui/Button';
import BlogOverview from '../../components/blog-seo/BlogOverview';
import ContentEngine from '../../components/blog-seo/ContentEngine';
import BlogPosts from '../../components/blog-seo/BlogPosts';
import BlogCalendar from '../../components/blog-seo/BlogCalendar';
import BlogConnections from '../../components/blog-seo/BlogConnections';
import BlogAnalytics from '../../components/blog-seo/BlogAnalytics';
import BlogPostEditor from '../../components/blog-seo/BlogPostEditor';
import SeoStudio from '../../components/blog-seo/SeoStudio';
import { blogService, BlogConnection } from '../../services/blogService';

type TabKey = 'overview' | 'engine' | 'posts' | 'calendar' | 'seo' | 'connections' | 'analytics';

const TABS: { key: TabKey; labelKey: string; fallback: string; shortKey: string; shortFallback: string; icon: React.FC<{ className?: string }> }[] = [
  { key: 'overview', labelKey: 'blogSeo.tabs.overview', fallback: 'Visão geral', shortKey: 'blogSeo.tabsShort.overview', shortFallback: 'Início', icon: LayoutDashboard },
  { key: 'engine', labelKey: 'blogSeo.tabs.engine', fallback: 'Motor de Conteúdo', shortKey: 'blogSeo.tabsShort.engine', shortFallback: 'Criar c/ IA', icon: Wand2 },
  { key: 'posts', labelKey: 'blogSeo.tabs.posts', fallback: 'Posts', shortKey: 'blogSeo.tabsShort.posts', shortFallback: 'Posts', icon: FileText },
  { key: 'calendar', labelKey: 'blogSeo.tabs.calendar', fallback: 'Calendário', shortKey: 'blogSeo.tabsShort.calendar', shortFallback: 'Agenda', icon: CalendarDays },
  { key: 'seo', labelKey: 'blogSeo.tabs.seo', fallback: 'SEO & Auditoria', shortKey: 'blogSeo.tabsShort.seo', shortFallback: 'SEO', icon: Gauge },
  { key: 'connections', labelKey: 'blogSeo.tabs.connections', fallback: 'Conexões', shortKey: 'blogSeo.tabsShort.connections', shortFallback: 'Conexões', icon: Plug },
  { key: 'analytics', labelKey: 'blogSeo.tabs.analytics', fallback: 'Acessos & Tráfego', shortKey: 'blogSeo.tabsShort.analytics', shortFallback: 'Acessos', icon: Activity }
];

const BlogSeoPage: React.FC = () => {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const active = (TABS.find(tb => tb.key === tab)?.key || 'overview') as TabKey;

  const [editorOpen, setEditorOpen] = useState(false);
  const [connections, setConnections] = useState<BlogConnection[]>([]);

  useEffect(() => {
    blogService.listConnections().then((r) => setConnections(r?.data ?? [])).catch(() => {});
  }, []);

  const go = (key: string) => navigate(`/blog-seo/${key}`);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page header — same pattern as the Social Media area */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2 bg-gradient-to-br from-primary-600 to-purple-600 rounded-xl">
            <PenSquare className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
              {t('nav.blogSeo', 'Blog & SEO')}
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 hidden sm:block">
              {t('blogSeo.pageSubtitle', 'Gerencie seu conteúdo e SEO')}
            </p>
          </div>
        </div>
        <Button variant="primary" className="gap-1.5 whitespace-nowrap flex-shrink-0" onClick={() => setEditorOpen(true)} aria-label={t('blogSeo.newPost', 'Nova postagem')}>
          <Plus className="w-4 h-4" />
          <span className="sm:hidden">{t('blogSeo.newPostShort', 'Novo')}</span>
          <span className="hidden sm:inline">{t('blogSeo.newPost', 'Nova postagem')}</span>
        </Button>
      </div>

      {/* Tab bar — mobile/tablet: grid of icon + short label (all tabs visible, no horizontal scroll) */}
      <nav className="lg:hidden grid grid-cols-4 sm:grid-cols-7 gap-1.5">
        {TABS.map(({ key, labelKey, fallback, shortKey, shortFallback, icon: Icon }) => {
          const isActive = active === key;
          return (
            <button
              key={key}
              onClick={() => go(key)}
              aria-label={t(labelKey, fallback)}
              aria-current={isActive ? 'page' : undefined}
              className={`min-w-0 flex flex-col items-center justify-center gap-0.5 px-1 py-1.5 rounded-lg text-[11px] leading-tight font-medium transition-colors ${
                isActive
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 ring-1 ring-primary-300 dark:ring-primary-700'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
              }`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="w-full truncate text-center">{t(shortKey, shortFallback)}</span>
            </button>
          );
        })}
      </nav>

      {/* Tab bar — desktop */}
      <div className="hidden lg:block border-b border-gray-200 dark:border-gray-700">
        <nav className="flex flex-wrap gap-1 -mb-px">
          {TABS.map(({ key, labelKey, fallback, icon: Icon }) => {
            const isActive = active === key;
            return (
              <button
                key={key}
                onClick={() => go(key)}
                className={`flex items-center gap-2 whitespace-nowrap px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t(labelKey, fallback)}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      <div>
        {active === 'overview' && <BlogOverview onNavigate={go} />}
        {active === 'engine' && <ContentEngine />}
        {active === 'posts' && <BlogPosts />}
        {active === 'calendar' && <BlogCalendar />}
        {active === 'seo' && <SeoStudio />}
        {active === 'connections' && <BlogConnections />}
        {active === 'analytics' && <BlogAnalytics />}
      </div>

      {/* New post editor (opened from the header button, on any tab) */}
      <BlogPostEditor
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        post={null}
        connections={connections}
        onSaved={() => { setEditorOpen(false); go('posts'); }}
      />
    </div>
  );
};

export default BlogSeoPage;
